import { signal } from '@preact/signals-react';
import type { Moment } from 'moment';

// Direct-on-calendar range picker used while booking from a caller (Caller V2
// detail tab). The user clicks a start cell then an end cell in the same staff
// column; the covered 15' slots become the booking's staff + startTime + duration.

const SLOT_MINUTES = 15;

export type ICalendarSelectCell = { staffId: string; time: Moment };
export type ICalendarSelectResult = { staffId: string; start: Moment; duration: number; token: number };

// Single-click on an empty calendar cell selects a range (vs the normal popover).
export const callerSelectActive = signal<boolean>(false);
// First (start) click; null once nothing/both are resolved.
export const callerSelectStart = signal<ICalendarSelectCell | null>(null);
// Covered-cell keys for highlighting.
export const callerSelectHighlight = signal<Set<string>>(new Set<string>());
// Finalized range the composer applies. `token` bumps each finalize so an
// identical range still re-applies.
export const callerSelectResult = signal<ICalendarSelectResult | null>(null);

let finalizeToken = 0;

const cellKey = (staffId: string, time: Moment) => `${time.format('DD-MM-YYYY HH:mm')}|${staffId}`;

export const isCellSelected = (staffId: string, time?: Moment) =>
  !!time && callerSelectHighlight.value.has(cellKey(staffId, time));

// Enable/disable the mode. Disabling clears any in-progress or finalized state so
// the calendar returns to normal and no highlight lingers.
export const setCallerSelectActive = (on: boolean) => {
  callerSelectActive.value = on;
  if (!on) {
    callerSelectStart.value = null;
    callerSelectHighlight.value = new Set<string>();
    callerSelectResult.value = null;
  }
};

// Handle a calendar cell click while the mode is active.
export const pickCallerCell = (staffId: string, time: Moment) => {
  const start = callerSelectStart.value;

  // No start yet, or a click in a different staff column → (re)start here.
  if (!start || start.staffId !== staffId) {
    callerSelectStart.value = { staffId, time: time.clone() };
    callerSelectHighlight.value = new Set<string>([cellKey(staffId, time)]);
    return;
  }

  // Second click in the same column → finalize the inclusive range.
  const a = start.time;
  const b = time;
  const startTime = a.isSameOrBefore(b) ? a.clone() : b.clone();
  const endTime = a.isSameOrBefore(b) ? b.clone() : a.clone();
  const duration = endTime.diff(startTime, 'minute') + SLOT_MINUTES; // inclusive of the end cell

  const highlight = new Set<string>();
  const cursor = startTime.clone();
  while (cursor.isSameOrBefore(endTime)) {
    highlight.add(cellKey(staffId, cursor));
    cursor.add(SLOT_MINUTES, 'minute');
  }
  callerSelectHighlight.value = highlight;
  callerSelectStart.value = null; // ready for a fresh selection

  finalizeToken += 1;
  callerSelectResult.value = { staffId, start: startTime, duration, token: finalizeToken };
};
